<template>
  <div class="brochures-page">
    <!-- 页面头部 -->
    <div class="page-header page-header-4">
      <div class="container">
        <h1 class="page-title">{{ pageTitle }}</h1>
        <div class="header-divider"></div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="page-content">
      <div class="container">
        <!-- 加载状态 -->
        <div v-if="isLoading" class="loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">{{ t('common.loading') }}</p>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="errorMessage" class="error-container">
          <div class="error-icon">
            <el-icon size="60"><Warning /></el-icon>
          </div>
          <h3 class="error-title">{{ t('common.error') }}</h3>
          <p class="error-message">{{ errorMessage }}</p>
          <button class="retry-button" @click="fetchPageData">
            {{ t('common.retry') }}
          </button>
        </div>

        <!-- 内容区域 -->
        <div v-else class="content-wrapper">
          <!-- 富文本内容（如果有） -->
          <div
            v-if="richtextHtml && richtextHtml.trim()"
            class="richtext-content"
            v-html="richtextHtml"
          ></div>

          <!-- 宣传册栅栏布局 -->
          <div v-if="brochuresList && brochuresList.length > 0" class="brochures-section">
            <!-- <h2 class="section-title">{{ t('brochures.title') }}</h2> -->
            
            <div class="brochures-grid">
              <div
                v-for="(item, index) in brochuresList"
                :key="index"
                class="brochure-card"
                @click="openPdfPreview(item)"
              >
                <div class="brochure-cover">
                  <img
                    :src="item.cover"
                    :alt="item.name"
                    class="cover-image"
                    @error="handleCoverError"
                  />
                  <div class="cover-overlay">
                    <el-icon size="32" color="white"><View /></el-icon>
                  </div>
                </div>
                <div class="brochure-info">
                  <h3 class="brochure-name">{{ item.name }}</h3>
                  <div class="brochure-action">
                    <el-icon size="16"><Document /></el-icon>
                    <span>{{ t('common.view') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 空状态 -->
          <div v-else class="empty-state">
            <div class="empty-icon">
              <el-icon size="80" color="var(--el-color-primary-light-8)">
                <Document />
              </el-icon>
            </div>
            <h3 class="empty-title">{{ t('common.no_data') }}</h3>
            <p class="empty-description">{{ t('brochures.no_brochures') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- PDF预览弹窗 -->
    <el-dialog
      v-model="pdfDialogVisible"
      :title="currentPdfName"
      width="90%"
      height="90vh"
      top="3vh"
      class="pdf-preview-dialog"
      destroy-on-close
    >
      <div class="pdf-preview-container">
        <iframe
          :src="currentPdfUrl"
          frameborder="0"
          width="100%"
          height="75vh"
          class="pdf-preview-iframe"
        ></iframe>
      </div>
      <template #footer>
        <div class="pdf-dialog-footer">
          <el-button type="primary" @click="downloadCurrentPdf">
            <el-icon><Download /></el-icon>
            {{ t('common.download') }}
          </el-button>
          <el-button @click="pdfDialogVisible = false">
            {{ t('common.close') }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Warning, View, Document, Download } from '@element-plus/icons-vue'
import { getSinglePageContent } from "@/assets/api/website";

// 使用 i18n
const { locale, t } = useI18n()

// 响应式数据
const pageData = ref(null)
const isLoading = ref(false)
const errorMessage = ref('')
const pdfDialogVisible = ref(false)
const currentPdfUrl = ref('')
const currentPdfName = ref('')

// 计算属性
const pageTitle = computed(() => {
  return pageData.value?.page_name || t('menu.brochures')
})

const richtextHtml = computed(() => {
  return pageData.value?.content?.richtext || ''
})

const brochuresList = computed(() => {
  return pageData.value?.content?.brochures || []
})

// 获取页面数据
const fetchPageData = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    const currentLocale = locale.value
    const response = await getSinglePageContent({
      lang: currentLocale,
      path: '/brochures'
    })

    if (response && response.content) {
      pageData.value = response
    } else {
      pageData.value = null
    }
  } catch (error) {
    console.error('获取宣传册数据失败:', error)
    errorMessage.value = t('common.load_failed')
  } finally {
    isLoading.value = false
  }
}

// 打开PDF预览
const openPdfPreview = (item) => {
  if (!item.path) return
  currentPdfUrl.value = item.path
  currentPdfName.value = item.name || t('brochures.preview')
  pdfDialogVisible.value = true
}

// 下载当前PDF
const downloadCurrentPdf = () => {
  if (!currentPdfUrl.value) return
  
  const link = document.createElement('a')
  link.href = currentPdfUrl.value
  link.download = currentPdfUrl.value.split('/').pop() || 'brochure.pdf'
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

// 封面图加载失败处理
const handleCoverError = (event) => {
  event.target.style.display = 'none'
  const container = event.target.parentElement
  const placeholder = document.createElement('div')
  placeholder.className = 'cover-fallback'
  placeholder.innerHTML = `
    <el-icon size="48" color="var(--el-color-primary-light-6)">
      <Document />
    </el-icon>
  `
  container.appendChild(placeholder)
}

// 监听语言变化，重新加载数据
watch(locale, () => {
  fetchPageData()
})

// 组件挂载时获取数据
onMounted(() => {
  fetchPageData()
})
</script>

<style scoped lang="scss">
@import '@/assets/css/common';

.brochures-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f9fbf7 0%, #ffffff 100%);
}

// 页面头部样式
.page-header {
  padding: 200px 0 100px;
  background: url('@/assets/img/header.jpg') center center / cover no-repeat;
  position: relative;
  border-bottom: 1px solid rgba($primary-color, 0.1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: inherit;
    filter: blur(4px);
    z-index: 1;
    transform: scale(1.01);
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 140px 0 60px;
  }
}

.page-title {
  font-family: $font-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  text-align: center;
  margin-bottom: 20px;
  letter-spacing: 2px;
  text-shadow: 0 1px 2px $primary-color;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
    margin-bottom: 16px;
  }
}

.header-divider {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, $primary-color, $secondary-color);
  margin: 0 auto;
  border-radius: 2px;

  @media (max-width: 480px) {
    width: 60px;
    height: 3px;
  }
}

// 主要内容区域
.page-content {
  padding: 60px 0;

  @media (max-width: 768px) {
    padding: 40px 0;
  }

  @media (max-width: 480px) {
    padding: 30px 0;
  }
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 60px;
}

// 富文本内容样式
.richtext-content {
  background: $white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: $shadow-light;
  border: 1px solid rgba($primary-color, 0.1);
  line-height: 1.8;

  p {
    color: $gray-80;
    font-size: 1.125rem;
    text-align: justify;
    text-indent: 2em;

    &:last-child {
      margin-bottom: 0;
    }
  }

  @media (max-width: 768px) {
    padding: 30px 24px;

    p {
      font-size: 1rem;
    }
  }

  @media (max-width: 480px) {
    padding: 24px 20px;

    p {
      font-size: 0.9375rem;
    }
  }
}

// 宣传册区域
.brochures-section {
  .section-title {
    font-family: $font-serif;
    font-size: 1.75rem;
    font-weight: 600;
    color: $primary-color-2;
    text-align: center;
    margin-bottom: 40px;
    position: relative;
    padding-bottom: 16px;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 3px;
      background: linear-gradient(90deg, $primary-color, $secondary-color);
      border-radius: 2px;
    }

    @media (max-width: 768px) {
      font-size: 1.5rem;
      margin-bottom: 30px;
      padding-bottom: 12px;
    }

    @media (max-width: 480px) {
      font-size: 1.375rem;
      margin-bottom: 24px;
    }
  }
}

// 栅栏布局
.brochures-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 24px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 16px;
  }
}

// 宣传册卡片
.brochure-card {
  background: $white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: $shadow-light;
  border: 1px solid rgba($primary-color, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);

    .cover-overlay {
      opacity: 1;
    }

    .brochure-name {
      color: $primary-color;
    }
  }
}

.brochure-cover {
  position: relative;
  width: 100%;
  aspect-ratio: 3/4;
  overflow: hidden;
  background: $gray-10;

  .cover-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  .cover-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba($primary-color, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .cover-fallback {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, $gray-10, $gray-20);
  }
}

.brochure-info {
  padding: 20px;

  @media (max-width: 480px) {
    padding: 16px;
  }
}

.brochure-name {
  font-size: 1.125rem;
  font-weight: 600;
  color: $text-color;
  margin-bottom: 12px;
  line-height: 1.4;
  transition: color 0.3s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 10px;
  }
}

.brochure-action {
  display: flex;
  align-items: center;
  gap: 6px;
  color: $primary-color;
  font-size: 0.875rem;
  font-weight: 500;

  .el-icon {
    font-size: 1rem;
  }
}

// PDF预览弹窗
:deep(.pdf-preview-dialog) {
  .el-dialog__header {
    padding: 20px 24px;
    border-bottom: 1px solid #eee;

    .el-dialog__title {
      font-size: 1.125rem;
      font-weight: 600;
      color: $text-color;
    }
  }

  .el-dialog__body {
    padding: 0;
  }

  .el-dialog__footer {
    padding: 16px 24px;
    border-top: 1px solid #eee;
  }
}

.pdf-preview-container {
  width: 100%;
  height: 75vh;
  background: #f5f5f5;
}

.pdf-preview-iframe {
  display: block;
  height: 75vh;
  border: none;
}

.pdf-dialog-footer {
  display: flex;
  justify-content: center;
  gap: 16px;
  :deep(.el-button) {
    padding: 10px 24px;
  }
}

// 加载状态
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid rgba($primary-color, 0.1);
  border-top-color: $primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-text {
  font-size: 1rem;
  color: $text-light;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// 错误状态
.error-container {
  text-align: center;
  padding: 60px 20px;

  @media (max-width: 768px) {
    padding: 40px 20px;
  }
}

.error-icon {
  margin-bottom: 20px;
  color: #f56c6c;
}

.error-title {
  font-size: 1.5rem;
  color: #f56c6c;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.error-message {
  font-size: 1rem;
  color: $text-light;
  margin-bottom: 20px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.retry-button {
  background: $primary-color;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: $primary-light-3;
    transform: translateY(-2px);
  }
}

// 空状态
.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: $white;
  border-radius: 16px;
  box-shadow: $shadow-light;
  border: 1px solid rgba($primary-color, 0.1);

  @media (max-width: 768px) {
    padding: 60px 20px;
  }

  @media (max-width: 480px) {
    padding: 40px 16px;
  }
}

.empty-icon {
  margin-bottom: 24px;
  opacity: 0.7;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
}

.empty-title {
  font-size: 1.5rem;
  color: $text-color;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.empty-description {
  font-size: 1rem;
  color: $text-light;
  max-width: 400px;
  margin: 0 auto;
  line-height: 1.5;

  @media (max-width: 480px) {
    font-size: 0.9375rem;
  }
}
</style>
